import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ConfirmationModal from '../../components/modals/confirmationModal';
import UserQueriesTable from '../../components/shared/UserQueriesTable';
import Pagination from '../../components/shared/Pagination';
import SendMessageModal from '../../components/modals/SendMessageModal';
import Select from '../../components/shared/Select';
import { useOnClickOutside } from '../../utils/hooks';
import Senticon from '../../assets/icons/sent-msg.svg'
import { QueryService } from '../../services/QueryService';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { RefreshCw } from "lucide-react";


const styles = {
	mainButton:
		'inline-flex items-center space-x-2 bg-dark-blue max-w-[210px] h-[52px] py-5 px-4 text-base font-semibold leading-100 text-white rounded-md cursor-pointer',
	tableDiv: 'bg-white rounded-lg overflow-auto mb-14',
	topTableHead: 'px-4 py-4 text-lg leading-100 font-medium text-gray text-left',
	tableData: 'px-4 py-4 text-lg font-medium text-dark-blue leading-100 text-left'
};

// --- Filter Dropdown ---
function FilterDropdown({ value, onChange }) {
	const [open, setOpen] = useState(false);
	const panelRef = useRef(null);
	useOnClickOutside(panelRef, () => setOpen(false));

	useEffect(() => {
		const onKey = (e) => e.key === 'Escape' && setOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	const activeCount = (value.status.length || 0) + (value.format.length || 0);

	const toggleToken = (group, token) => {
		const set = new Set(value[group]);
		set.has(token) ? set.delete(token) : set.add(token);
		onChange({ ...value, [group]: Array.from(set) });
	};

	const clearAll = () => onChange({ status: [], format: [] });

	const allStatuses = ['SOLVED', 'PENDING'];

	return (
		<div className="relative" ref={panelRef}>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="inline-flex h-[52px] items-center gap-2 rounded-lg border border-[#F0F0F0] bg-white px-4 text-sm font-medium text-[#180A63]"
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
					<path
						d="M0.75 6.125H23.25V8.375H0.75V6.125ZM4.5 11.375H19.5V13.625H4.5V11.375ZM9 16.625H15V18.875H9V16.625Z"
						fill="#180A63"
					/>
				</svg>
				{activeCount > 0 && (
					<span className="ml-1 rounded-full bg-[#180A63] px-2 py-0.5 text-xs font-semibold text-white">
						{activeCount}
					</span>
				)}
			</button>

			{open && (
				<div
					role="menu"
					className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
				>
					{/* Status */}
					<div className="mb-3">
						<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
							Status
						</div>
						<div className="flex flex-wrap gap-2">
							{allStatuses.map((s) => (
								<button
									key={s}
									onClick={() => toggleToken('status', s)}
									className={`rounded-full border px-3 py-1 text-xs ${
										value.status.includes(s)
											? 'border-[#180A63] bg-[#180A63] text-white'
											: 'border-gray-300 bg-white text-gray-700'
									}`}
								>
									{s}
								</button>
							))}
						</div>
					</div>

					<div className="my-2 h-px w-full bg-gray-100" />
					<div className="mt-4 flex items-center justify-between">
						<button
							onClick={clearAll}
							className="text-sm font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
						>
							Clear all
						</button>
						{/* <button
							onClick={() => setOpen(false)}
							className="rounded-lg bg-[#180A63] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-95"
						>
							Apply
						</button> */}
					</div>
				</div>
			)}
		</div>
	);
}

export default function UserQueriesPage() {
	const [filters, setFilters] = useState({ status: [], format: [] });
	const [timeFilter, setTimeFilter] = useState('');

	// Pagination + data state
	const [page, setPage] = useState(1);
	const [limit] = useState(10);
	const [total, setTotal] = useState(0);
	const [userQueries, setUserQueries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [resolving, setResolving] = useState(false);

	const loadQueries = useCallback(
		async (pageArg = page, filtersArg = filters) => {
			try {
			setLoading(true);
			setError(null);

			const statusParam =
				filtersArg.status.length === 1 ? filtersArg.status[0].toLowerCase() : undefined;

			const data = await QueryService.getQueries(pageArg, limit, statusParam);

			const mapped = (data?.queries || []).map((q) => ({
				query_id: `#${q.queryId}`,
				email: q?.createdBy?.email || '-',
				message: q?.query || '-',
				status: (q?.status || '').toUpperCase(),
				solution: q?.solution || null,
				__raw: q,
			}));

			setUserQueries(mapped);
			setTotal(data?.pagination?.total || mapped.length);
			} catch (err) {
			setError(err?.response?.data?.message || err?.message || 'Failed to load queries');
			} finally {
			setLoading(false);
			}
		},
		[page, limit, filters] // <-- deps
	);

	useEffect(() => {
		loadQueries(page, filters);
	}, [page, limit, filters, loadQueries]);

	// Send flow state
	const [selectedQuery, setSelectedQuery] = useState(null);
	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [draftSolution, setDraftSolution] = useState('');

	const filteredUserQueries = useMemo(() => {
		// First, filter by time period
		const timeFiltered = userQueries.filter((q) => {
			// If no time filter is selected, show all queries
			if (!timeFilter) return true;
			
			if (!q.__raw?.createdDateTime) return true;
			
			const queryDate = new Date(q.__raw.createdDateTime);
			if (isNaN(queryDate.getTime())) return true;

			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth();

			switch (timeFilter) {
				case 'this-month': {
					return queryDate.getMonth() === currentMonth && queryDate.getFullYear() === currentYear;
				}
				case 'last-month': {
					const lastMonth = new Date(currentYear, currentMonth - 1, 1);
					const lastMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
					return queryDate >= lastMonth && queryDate <= lastMonthEnd;
				}
				case 'last-3-months': {
					const threeMonthsAgo = new Date(currentYear, currentMonth - 3, 1);
					return queryDate >= threeMonthsAgo && queryDate <= now;
				}
				default:
					return true;
			}
		});

		// Then, filter by status
		const statusFiltered = timeFiltered.filter((q) =>
			filters.status.length === 0 ? true : filters.status.includes(q.status)
		);

		// Finally, remove duplicates
		const seen = new Set();
		return statusFiltered.filter((q) => {
			const key = `${q.query_id}|${q.email}|${q.message}|${q.status}|${q.solution || ''}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [userQueries, filters, timeFilter]);

	const handleViewDetails = (query) => {
		setSelectedQuery(query);
		setDraftSolution(query.solution || '');
		setSendModalOpen(true);
	};

	// 1) Close compose modal, 2) open confirmation
	const handleSendMessage = (solutionText) => {
		setDraftSolution(solutionText);
		setSendModalOpen(false); // close first modal
		setConfirmOpen(true); // open "Message sent" modal
	};

	// Confirm -> call API to answer, then update UI on success
	const handleMarkAsSolved = async () => {
		if (!selectedQuery) return;
		try {
			setResolving(true);
			const id = selectedQuery?.__raw?._id || selectedQuery?._id;
			if (!id) throw new Error('Missing query id');
			await QueryService.answerQuery(id, draftSolution);
			setUserQueries((prev) =>
				prev.map((q) =>
					q.query_id === selectedQuery.query_id
						? { ...q, status: 'SOLVED', solution: draftSolution }
						: q
				)
			);
			setConfirmOpen(false);
		} catch (e) {
			setError(e?.response?.data?.message || e?.message || 'Failed to submit answer');
		}
		finally {
			setResolving(false);
		}
	};

	return (
		<div className="md:py-4 bg-bgColor">
			<div className="flex justify-between flex-col md:flex-row items-center mb-12">
				<div className="flex items-center gap-3">
					<h1 className="text-dark-blue font-bold text-main leading-100">
						User Queries
					</h1>
					<button
						onClick={loadQueries} // <-- your refresh function
						disabled={loading} // optional: disable when loading
						className="p-2 rounded-full bg-lightGrey hover:bg-gray-300 transition-colors disabled:opacity-50"
						title="Refresh"
					>
						<RefreshCw className={`w-5 h-5 text-dark-blue ${loading ? 'animate-spin' : ''}`} />
					</button>
				</div>

			<div className="flex space-x-1">
				<Select
					id="time-filter"
					value={timeFilter}
					onChange={(e) => {
						setTimeFilter(e.target.value);
						setPage(1); // reset to first page on time filter change
					}}
					placeholder="Select period"
					options={[
						{ value: 'this-month', label: 'This month' },
						{ value: 'last-month', label: 'Last month' },
						{ value: 'last-3-months', label: 'Last 3 months' }
					]}
					className="min-w-[170px]"
				/>
				{/* Filters dropdown */}
				<FilterDropdown
					value={filters}
					onChange={(next) => {
						setFilters(next);
						setPage(1); // reset to first page on filter change
					}}
				/>
			</div>
			</div>


		{/* Keep pagination mounted; only switch the table area */}
		{error ? (
			<div className="bg-white rounded-lg p-6 text-center text-red-600">{error}</div>
		) : loading ? (
			<div className="bg-white rounded-lg overflow-auto mb-14">
				<table className="min-w-[700px] xl:min-w-full">
				<thead className="bg-[#F2F3F7]">
					<tr>
						<th scope="col" className={styles.topTableHead}>
							QUERY ID
						</th>
						<th scope="col" className={styles.topTableHead}>
							EMAIL
						</th>
						<th scope="col" className={styles.topTableHead}>
							MESSAGE
						</th>
						<th scope="col" className={styles.topTableHead}>
							STATUS
						</th>
						<th scope="col" className={styles.topTableHead}>
							ACTIONS
						</th>
					</tr>
				</thead>
					<tbody className="bg-white">
						{Array.from({ length: 10 }).map((_, i) => (
							<tr key={`skeleton-row-${i}`}>
								<td className="px-4 py-4"><Skeleton width={90} height={20} /></td>
								<td className="px-4 py-4"><Skeleton width={220} height={20} /></td>
								<td className="px-4 py-4"><Skeleton width={420} height={20} /></td>
								<td className="px-5 py-4"><Skeleton width={80} height={28} /></td>
								<td className="px-5 py-4"><Skeleton width={90} height={20} /></td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		) : filteredUserQueries.length === 0 ? (
			<div className="bg-white rounded-lg p-12 text-center mb-14">
				<h3 className="text-xl font-semibold text-dark-blue mb-2">No Queries Found</h3>
				<p className="text-base text-gray-600">
					No queries match your current filters. Please adjust your filter criteria.
				</p>
			</div>
		) : (
			<UserQueriesTable userQueries={filteredUserQueries} onViewDetails={handleViewDetails} />
		)}
		
		{/* Only show pagination if there are results */}
		{!loading && !error && filteredUserQueries.length > 0 && (
			<Pagination totalDocs={total} docsPerPage={limit} onPageChange={(p) => setPage(p)} />
		)}

			{/* Compose / View Solution modal */}
			<SendMessageModal
				isOpen={sendModalOpen}
				onClose={() => setSendModalOpen(false)}
				isImported={false}
				query={selectedQuery}
				onSend={handleSendMessage}
			/>

			{/* Message sent confirmation */}
			<ConfirmationModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				icon={<img src={Senticon} alt="Sent" className="mx-auto" />}
				heading={
					<p className="italic">
							Mark ticket as resolved?
					</p>
				}
				description={
					<p className="text-[18px] italic text-center text-gray-600">
					Submitting this response will mark the ticket as resolved and notify the requester.
					</p>
				}
				primaryButtonText={resolving ? "Resolving..." : "Mark as solved"} 
				secondaryButtonText="Cancel"
				onPrimaryClick={handleMarkAsSolved}
				onSecondaryClick={() => setConfirmOpen(false)}
				primaryDisabled={resolving}
				/>


		</div>
	);
}
